import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import api from "../../services/api";
import { buildAuthOverlayUrl } from "../../utils/authNavigation";
import { VoiceGuidanceProvider } from "../../context/VoiceGuidanceContext";
import { useRoleLocationSync } from "../../hooks/useRoleLocationSync";
import RoleSidebar from "../shared/RoleSidebar";
import VoiceGuidanceToolbar from "../shared/VoiceGuidanceToolbar";
import NotificationToolbarBell from "../shared/NotificationToolbarBell";
import AppBottomDock from "../shared/AppBottomDock";
import CustomerAppHeader from "./CustomerAppHeader";
import CaregiverSubscriptionGate from "../caregiver/CaregiverSubscriptionGate";
import { initialSidebarExpanded } from "../../utils/layoutSidebar";
import "./customerLayot.css";

function homePathForRole(role) {
  if (role === "CUSTOMER") return "/dashboard";
  if (role === "CAREGIVER") return "/caregiver";
  if (role === "CARE_MANAGER") return "/care-manager";
  if (role === "ADMIN" || role === "OPS") return "/admin";
  return "/login";
}

function expectedRoleForLayout(layoutRole) {
  if (layoutRole === "customer") return "CUSTOMER";
  if (layoutRole === "caregiver") return "CAREGIVER";
  return null;
}

function CustomerLayout({ role = "customer", hideSidebar = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(() => {
    if (typeof window !== "undefined" && window.location.pathname === "/marketplace") {
      return false;
    }
    if (role === "customer") return true;
    return initialSidebarExpanded();
  });
  const voiceShell = role === "customer" || role === "caregiver";
  const authRole = api.getAuthRoleFromToken?.() || "";
  const expectedRole = expectedRoleForLayout(role);
  const roleMismatch = Boolean(expectedRole && authRole && authRole !== expectedRole);

  useRoleLocationSync(role === "caregiver" ? "caregiver" : role === "customer" ? "customer" : null, {
    enabled: (role === "customer" || role === "caregiver") && !roleMismatch,
  });

  // Find caregivers needs max content width — start with the compact icon rail.
  useEffect(() => {
    if (location.pathname !== "/marketplace") return;
    setSidebarExpanded(false);
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (role !== "customer" || roleMismatch) return undefined;
    const apply = (settings) => {
      document.documentElement.dataset.theme = "light";
      try {
        window.localStorage.removeItem("nema_theme");
        if (settings?.dateFormat) window.localStorage.setItem("nema_date_format", settings.dateFormat);
      } catch {
        /* ignore private mode */
      }
    };
    try {
      apply({
        dateFormat: window.localStorage.getItem("nema_date_format") || "DD MMM YYYY",
      });
    } catch {
      /* ignore */
    }
    let cancelled = false;
    api.getCustomerSettings?.()
      .then((res) => {
        if (!cancelled) apply(res?.settings || {});
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [role, roleMismatch, location.pathname]);

  useEffect(() => {
    if (role !== "caregiver" || roleMismatch) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const pause = await api.getMyEmergencyPause?.();
        const roleVal = pause?.serviceRole;
        if (!cancelled && roleVal) {
          window.localStorage.setItem("nema_caregiver_service_role", String(roleVal));
          return;
        }
      } catch {
        /* fall through */
      }
      try {
        const res = await api.getCompleteCaregiverProfile();
        const data = res?.profile || res;
        const roleVal =
          data?.serviceRole || data?.profileExtended?.marketplacePrefs?.serviceRole || "";
        if (!cancelled && roleVal) {
          window.localStorage.setItem("nema_caregiver_service_role", String(roleVal));
        }
      } catch {
        /* keep cached */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [role, roleMismatch, location.pathname]);

  useEffect(() => {
    const token = api.getToken?.();
    const params = new URLSearchParams(location.search);
    const authOverlay = params.get("auth") === "login";
    const isPublicMarketplace =
      location.pathname === "/marketplace" || location.pathname === "/book-care";

    if (!token) {
      // Guest marketplace browse and in-page register/login overlays must not bounce away.
      if (authOverlay || isPublicMarketplace) return;
      navigate(
        buildAuthOverlayUrl("/", {
          returnTo: location.pathname || "/marketplace",
          tab: "create",
        }),
        { replace: true }
      );
      return;
    }
    if (roleMismatch) {
      navigate(homePathForRole(authRole), { replace: true });
    }
  }, [navigate, role, roleMismatch, authRole, location.pathname, location.search]);

  function isDesktopViewport() {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(min-width: 901px)").matches;
  }

  function handleMenuToggle() {
    if (isDesktopViewport()) {
      setSidebarExpanded((s) => !s);
      return;
    }
    setSidebarOpen((s) => !s);
  }

  const hasToken = Boolean(api.getToken?.());
  const isProfileEditor = /\/caregiver\/complete-profile\/?$/.test(location.pathname);
  const isCareHubPortal = /^\/carehub(\/|$)/.test(location.pathname);
  const isMarketplaceBrowseRoute = location.pathname === "/marketplace";
  const isMarketplaceBookingRoute = location.pathname.startsWith("/marketplace/");
  const showMarketplaceChrome = hasToken && isMarketplaceBrowseRoute;
  const hideChrome =
    isProfileEditor || isCareHubPortal || (isMarketplaceBookingRoute && !showMarketplaceChrome);
  const showSidebar = !hideSidebar && !hideChrome;
  const layout = (
    <div
      className={`customer-layout ${sidebarExpanded ? "customer-layout--expanded" : "customer-layout--compact"}${
        hideSidebar ? " customer-layout--no-sidebar" : ""
      }${isProfileEditor ? " customer-layout--profile-editor" : ""}${isCareHubPortal ? " customer-layout--carehub" : ""}${
        isMarketplaceBookingRoute && !showMarketplaceChrome ? " customer-layout--marketplace" : ""}`}
    >
      {sidebarOpen && showSidebar ? (
        <button
          type="button"
          className="layout-overlay"
          aria-label="Close menu overlay"
          onClick={() => setSidebarOpen(false)}
        />
      ) : null}
      {showSidebar ? (
        <RoleSidebar
          role={role}
          isOpen={sidebarOpen}
          isExpanded={sidebarExpanded}
          onClose={() => setSidebarOpen(false)}
          onToggleExpand={handleMenuToggle}
        />
      ) : null}
      <div className="customer-layout__main">
        {!hideChrome && role === "customer" ? (
          <CustomerAppHeader onMenuToggle={showSidebar ? handleMenuToggle : null} />
        ) : !hideChrome ? (
          <div className="customer-layout__toolbar">
            {showSidebar ? (
              <button
                type="button"
                className="layout-hamburger layout-hamburger--toolbar"
                aria-label="Open menu"
                onClick={handleMenuToggle}
              >
                ☰
              </button>
            ) : null}
            <div className="customer-layout__toolbar-actions">
              {voiceShell ? <VoiceGuidanceToolbar variant="toolbar" /> : null}
              <NotificationToolbarBell />
            </div>
          </div>
        ) : null}
        <div className="customer-layout__content">
          {roleMismatch ? null : <Outlet />}
        </div>
        {role === "caregiver" ? <CaregiverSubscriptionGate /> : null}
      </div>
      {!hideChrome ? <AppBottomDock /> : null}
    </div>
  );

  if (voiceShell) {
    return <VoiceGuidanceProvider>{layout}</VoiceGuidanceProvider>;
  }

  return layout;
}

export default CustomerLayout;
